/**
 * ScrollReveal — CSS-only ersättning för tidigare framer-motion-version.
 *
 * Använder IntersectionObserver för att toggle:a en `data-revealed`-flagga
 * och CSS för transitions. Stödjer samma API som tidigare så call-sites
 * inte behöver ändras. Respekterar prefers-reduced-motion (då visas
 * elementet utan animation).
 *
 * Tar bort framer-motion från initial-bundlen — stor mobil-vinst.
 */
import { useEffect, useRef, type ReactNode } from "react";

type AnimationVariant =
  | "fade-up"
  | "fade-in"
  | "scale-in"
  | "slide-left"
  | "slide-right";

interface ScrollRevealProps {
  children: ReactNode;
  variant?: AnimationVariant;
  delay?: number;
  duration?: number;
  className?: string;
  once?: boolean;
}

function useReveal<T extends HTMLElement>(once = true) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.dataset.revealed = "true";
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.setAttribute("data-revealed", "true");
            if (once) io.unobserve(e.target);
          } else if (!once) {
            e.target.removeAttribute("data-revealed");
          }
        }
      },
      { rootMargin: "-60px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once]);
  return ref;
}

export const ScrollReveal = ({
  children,
  variant = "fade-up",
  delay = 0,
  duration = 0.6,
  className = "",
  once = true,
}: ScrollRevealProps) => {
  const ref = useReveal<HTMLDivElement>(once);
  return (
    <div
      ref={ref}
      data-reveal={variant}
      style={{
        transitionDuration: `${duration}s`,
        transitionDelay: `${delay}s`,
      }}
      className={className}
    >
      {children}
    </div>
  );
};

interface StaggerContainerProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
}

export const StaggerContainer = ({
  children,
  className = "",
  staggerDelay = 0.1,
}: StaggerContainerProps) => {
  const ref = useReveal<HTMLDivElement>(true);
  return (
    <div
      ref={ref}
      data-reveal="stagger"
      style={{ ["--reveal-stagger" as string]: `${staggerDelay}s` }}
      className={className}
    >
      {children}
    </div>
  );
};

export const StaggerItem = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div data-reveal-item className={className}>
    {children}
  </div>
);

export const CountUp = ({ value, suffix = "" }: { value: string; suffix?: string }) => {
  const ref = useReveal<HTMLSpanElement>(true);
  return (
    <span ref={ref} data-reveal="scale-in" style={{ transitionDuration: "0.5s" }}>
      {value}
      {suffix}
    </span>
  );
};
